// components/layout/Navigation.tsx
import { useState, useEffect } from 'react';
import { ChevronDown, Menu, MoveRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useLocation } from 'react-router';

// Import logo
const logoImage = new URL('../assets/logos/fat-logo.webp', import.meta.url).href;


const services = [
  // // Column 1 (5 items)
  // { name: 'Corporate Structuring', slug: 'corporate-structuring', column: 1 },
  // { name: 'International Litigation', slug: 'international-litigation', column: 1 },
  { name: 'Commercial Litigation', slug: 'commercial-litigation', column: 1, 
    // highlight: true
   },
  { name: 'Debt Recovery', slug: 'debt-recovery', column: 1,
    //  highlight: true
    },
  { name: 'Mediation', slug: 'mediation', column: 1 },
  //  { name: 'Investment Solutions', slug: 'investment-solutions', column: 1 },
  { name: 'Arbitration', slug: 'arbitration', column: 1 },
  { name: 'Contract Drafting & Support', slug: 'contract-drafting-&-support', column: 1 },

  // Column 2 (4 items)
  { name: 'Corporate Governance Services', slug: 'corporate-governance-services', column: 2 },
  { name: 'Trademark Registration', slug: 'trademark-registration', column: 2 },
  { name: 'Regulatory Compliance', slug: 'regulatory-compliance', column: 2 },
  { name: 'Investment Solutions', slug: 'investment-solutions', column: 2 },

  // Column 3 (4 items)
  { name: 'Foreign Company Incorporation', slug: 'foreign-company-incorporation', column: 3 },
  { name: 'Local Company Incorporation', slug: 'local-company-incorporation', column: 3 },
  { name: 'Mergers and Acquisitions', slug: 'mergers-&-acquisitions', column: 3 },
  { name: 'Corporate Restructuring', slug: 'corporate-restructuring', column: 3 },
];

// About Us dropdown items - simple list with direct paths
const aboutItems = [
  { name: 'Who We Are', path: '/top-Law-firm-in-KSA' },
  { name: 'Values', path: '/our-value' },
  // { name: 'Our Team', path: '/tamimi-lawfirm-team' },
  { name: 'Areas Of Practice', path: '/areas-of-practice' },
  { name: 'Accreditations', path: '/accreditations' },
  { name: 'Vision 2030', path: '/vision-2030' },
  { name: 'Invest in Saudi Arabia', path: '/invest-in-saudi-arabia' }
];

const scrollToElement = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const offset = 100; 
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showServicesMega, setShowServicesMega] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const location = useLocation();
  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Close mobile on desktop */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false);
        setMobileServicesOpen(false);
        setMobileAboutOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* Prevent body scroll */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setMobileAboutOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleRouteChange = () => {
      setShowServicesMega(false);
      setShowAboutDropdown(false);
    };
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        scrollToElement(id);
      }, 500);
    }
  }, [location.hash]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    
    if (isLandingPage) {
      scrollToElement(hash);
    } else {
      window.location.href = `/#${hash}`;
    }
  };

  return (
    <>
      {/* NAVBAR */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 transition-all duration-300
           ${mobileOpen ? 'z-10' : 'z-50'}
          ${
            isScrolled
              ? 'bg-[#0a0a0a]/95 shadow-lg backdrop-blur-xl'
              : 'bg-transparent shadow-none'
          }
        `}
      >
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="flex h-20 lg:h-24 items-center justify-between">
            <Link to="/" aria-label="Fahad Al Tamimi Law Firm - Home" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
             <img
  src={logoImage}
  alt="Fahad Al Tamimi Law Firm"
  width="240"
  height="60"
  fetchPriority="high"
  className="h-10 sm:h-12 lg:h-14 w-auto logo"
/>
            </Link>
            <div className="hidden lg:flex flex-1 justify-center gap-6">
              {/* Services Mega Menu */}
              <div
                onMouseEnter={() => setShowServicesMega(true)}
                onMouseLeave={() => setShowServicesMega(false)}
                className="relative"
              >
                <button 
                  className="flex items-center gap-2 uppercase tracking-wider text-gray-200 hover:text-[#c3a565] cursor-pointer navbar-navitems"
                >
                  Our Services
                  <ChevronDown
                    className={`transition ${
                      showServicesMega ? 'rotate-180 text-[#c3a565]' : ''
                    }`}
                  />
                </button>
              </div>

              {/* About Us Dropdown */}
              <div
                onMouseEnter={() => setShowAboutDropdown(true)}
                onMouseLeave={() => setShowAboutDropdown(false)}
                className="relative"
              >
                <button 
                  className="flex items-center gap-2 uppercase tracking-wider text-gray-200 hover:text-[#c3a565] cursor-pointer navbar-navitems"
                >
                  About Us
                  <ChevronDown
                    className={`transition ${
                      showAboutDropdown ? 'rotate-180 text-[#c3a565]' : ''
                    }`}
                  />
                </button>

                {/* Simple Dropdown Menu */}
                <AnimatePresence>
                  {showAboutDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full mt-2 w-64 bg-[#1a1a1a] rounded-lg overflow-hidden shadow-xl"
                    >
                      <div className="py-2">
                        {aboutItems.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="block px-4 py-2 text-gray-300 hover:text-[#c3a565] hover:bg-[#2a2a2a] transition-colors"
                            onClick={() => setShowAboutDropdown(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/blog"
                className="uppercase text-gray-200 hover:text-[#c3a565] cursor-pointer navbar-navitems"
              >
                Insights
              </Link>

              {/* Our Expertise Link */}
              <Link
                to="/our-associates"
                // onClick={(e) => {
                //   if (isLandingPage) {
                //     e.preventDefault();
                //     scrollToElement('our-associates');
                //   }
                // }}
                className="uppercase text-gray-200 hover:text-[#c3a565] cursor-pointer navbar-navitems"
              >
                Our Associates 
              </Link>

              {/* Our Process Link */}
              {/* <Link
                to={isLandingPage ? '#our-process' : '/#our-process'}
                onClick={(e) => {
                  if (isLandingPage) {
                    e.preventDefault();
                    scrollToElement('our-process');
                  }
                }}
                className="uppercase text-gray-200 hover:text-[#c3a565] cursor-pointer navbar-navitems"
              >
                Our Process
              </Link> */}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:block">
              <Link
                to="/contact-us"
                // onClick={(e) => {
                //   if (isLandingPage) {
                //     e.preventDefault();
                //     scrollToElement('contact');
                //   }
                // }}
                className="rounded-full bg-gradient-to-r from-[#c3a565] to-[#d4b676] px-8 py-4 font-semibold text-black"
              >
                Book Consultation
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
  type="button"
  className="lg:hidden cursor-pointer text-gray-200"
  onClick={() => setMobileOpen(true)}
  aria-label="Open navigation menu"
>
  <Menu size={28} aria-hidden="true" />
</button>
          </div>
        </div>

        {/* DESKTOP SERVICES MEGA MENU */}
        <AnimatePresence>
          {showServicesMega && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 top-full z-40 bg-[#1a1a1a]"
              onMouseEnter={() => setShowServicesMega(true)}
              onMouseLeave={() => setShowServicesMega(false)}
            >
              <div className="mx-auto max-w-[1800px] p-5">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {/* IMAGE */}
                  <div className="relative rounded-2xl overflow-hidden min-h-[260px]">
                    <img
                      src="https://www.tamimilawfirm.com/blog/wp-content/uploads/2022/09/lawfirm-1.jpg"
                      alt="Law firm interior"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent"></div>
                    <div className="absolute bottom-4 left-10">
                      <span className="text-[#c3a565] text-sm font-semibold">Fahad Al Tamimi</span>
                      <p className="text-white text-xs">Legal Excellence Since 2008</p>
                    </div>
                  </div>

                  {/* COLUMN 1 */}
                  <div className="flex flex-col gap-1">
                  {services.filter(s => s.column === 1).map((service) => (
                    <MegaItem 
                      key={service.slug}
                      title={service.name} 
                      href={`/${service.slug}`}
                      // highlight={service.highlight}
                      onClick={() => setShowServicesMega(false)}
                    />
                  ))}
                  </div>

                  {/* COLUMN 2 */}
                  <div className="flex flex-col gap-1">
                    {services.filter(s => s.column === 2).map((service) => (
                      <MegaItem 
                        key={service.slug}
                        title={service.name} 
                        // highlight={service.highlight}
                        // href={`/services/${service.slug}`}
                         href={`/${service.slug}`}
                        onClick={() => setShowServicesMega(false)}
                      />
                    ))}
                  </div>

                  {/* COLUMN 3 */}
                  <div className="flex flex-col gap-1">
                    {services
                      .filter(s => s.column === 3)
                      .map((service) => (
                        <MegaItem
                          key={service.slug}
                          title={service.name}
                          // highlight={service.highlight}
                          // href={`/services/${service.slug}`}
                           href={`/${service.slug}`}
                          onClick={() => setShowServicesMega(false)}
                        />
                      ))}

                    <Link
                      to="/services"
                      onClick={() => setShowServicesMega(false)}
                      className="flex items-center gap-2 py-2 text-[#c3a565] transition-colors"
                    >
                      View All Services
                      <MoveRight />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* MOBILE OVERLAY */}
      <AnimatePresence mode="wait">
        {mobileOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />

            {/* Drawer */}
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{
                type: 'spring',
                stiffness: 260,
                damping: 30,
              }}
              className="fixed right-0 top-0 h-full w-[100%] max-w-md flex flex-col"
              style={{ background: '#1a1a1a', width: '300px',zIndex:'1000' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-800">
                <span className="text-lg font-semibold text-[#c3a565]">Menu</span>
                <button onClick={() => setMobileOpen(false)} className="cursor-pointer">
                  <X size={24} className="text-gray-200" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Our Services */}
                <button
                  onClick={() => {
                    setMobileServicesOpen(!mobileServicesOpen);
                    setMobileAboutOpen(false);
                  }}
                  className="flex w-full items-center justify-between py-1 text-gray-200 uppercase cursor-pointer navbar-navitems"
                >
                  Our Services
                  <motion.div
                    animate={{ rotate: mobileServicesOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className={mobileServicesOpen ? 'text-[#c3a565]' : ''} />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {mobileServicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 pl-4 border-l border-gray-800 mb-4">
                        {services.map((service) => (
                          <MobileItem
                            key={service.slug}
                            title={service.name}
                            //  highlight={service.highlight}
                            // href={`/services/${service.slug}`}
                            href={`/${service.slug}`}
                            close={() => setMobileOpen(false)}
                          />
                        ))}
                        <Link
                          to="/services"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2 py-2 text-[#c3a565] transition-colors"
                        >
                          View All Services
                          <MoveRight />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* About Us Dropdown */}
                <button
                  onClick={() => {
                    setMobileAboutOpen(!mobileAboutOpen);
                    setMobileServicesOpen(false);
                  }}
                  className="flex w-full items-center justify-between py-1 text-gray-200 uppercase cursor-pointer navbar-navitems"
                >
                  About Us
                  <motion.div
                    animate={{ rotate: mobileAboutOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className={mobileAboutOpen ? 'text-[#c3a565]' : ''} />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {mobileAboutOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 pl-4 border-l border-gray-800 mb-4">
                        {aboutItems.map((item) => (
                          <MobileItem
                            key={item.name}
                            title={item.name}
                            href={item.path}
                            close={() => setMobileOpen(false)}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                  <Link
                to="/blog"
                 onClick={() => setMobileOpen(false)}
                 className="flex w-full items-center justify-between py-1 text-gray-200 uppercase cursor-pointer hover:text-[#c3a565] transition-colors navbar-navitems"
              >
                Insights
              </Link>


                {/* Our Expertise Link */}
                <Link
                   to="/our-associates"
                  onClick={() => setMobileOpen(false)}
                  className="flex w-full items-center justify-between py-1 text-gray-200 uppercase cursor-pointer hover:text-[#c3a565] transition-colors navbar-navitems"
                >
                  Our  Associates
                </Link>

                {/* Our Process Link */}
                {/* <Link
                  to="/#our-process"
                  onClick={() => setMobileOpen(false)}
                  className="flex w-full items-center justify-between py-1 text-gray-200 uppercase cursor-pointer hover:text-[#c3a565] transition-colors navbar-navitems"
                >
                  Our Process
                </Link> */}
              </div>

              {/* CTA */}
              <div className="p-6 border-t border-gray-800">
                <Link
                  to="/contact-us"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full text-center rounded-full bg-gradient-to-r from-[#c3a565] to-[#d4b676] py-4 font-semibold text-black hover:shadow-lg hover:shadow-[#c3a565]/25 transition-all duration-300"
                >
                  Book Consultation
                </Link>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

/* Mega Menu Item Component */
const MegaItem = ({ title, href, onClick, highlight }: { title: string; href: string;  highlight?: boolean; onClick: () => void }) => (
  <Link
    to={href}
    onClick={onClick}
    className={`block py-2 transition-colors ${
      highlight
        ? 'text-[#c3a565] font-semibold'
        : 'text-gray-300 hover:text-[#c3a565]'
    }`}
  >
    {title}
  </Link>
);

/* Mobile Menu Item Component */
const MobileItem = ({
  title,
  href,
  close,
  highlight,
}: {
  title: string;
  href: string;
  close: () => void;
  highlight?: boolean;
}) => (
  <Link
    to={href}
    onClick={close}
    className={`block py-2 transition-colors ${
      highlight
        ? 'text-[#c3a565] font-semibold'
        : 'text-gray-300 hover:text-[#c3a565]'
    }`}
  >
    {title}
  </Link>
);